// =============================================
// ReportsTab — YKV dashboard: stats, due-for-review, drill-down → Notes
// =============================================
import React, { useMemo, useState } from 'react';
import { FiSearch, FiStar, FiFileText, FiFolder, FiHardDrive, FiBell, FiClock, FiCheck } from 'react-icons/fi';
import {
  NoteRow, CategoryRow, LinkRow, NotesBundle, formatBytes, buildCategoryTree, flattenTree, reviewDue,
  updateNote, nextReviewPatch,
} from './notesLib';
import { MediaMeta } from './notesMedia';

interface Props {
  notes: NoteRow[];
  categories: CategoryRow[];
  links: LinkRow[];
  media: MediaMeta[];
  onReload: () => Promise<NotesBundle>;
  onOpenNote: (id: number) => void;
  onOpenCategory: (id: number) => void;
}

function when(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso); return isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

const ReportsTab: React.FC<Props> = ({ notes, categories, links, media, onReload, onOpenNote, onOpenCategory }) => {
  const [search, setSearch] = useState('');
  const now = useMemo(() => new Date(), []);
  const markReviewed = async (n: NoteRow) => { await updateNote(n.note_id, nextReviewPatch(n)); await onReload(); };

  const mediaBytes = useMemo(() => media.reduce((s, m) => s + (Number(m.size_bytes) || 0), 0), [media]);
  const totalBytes = useMemo(() => notes.reduce((s, n) => s + (Number(n.size_bytes) || 0), 0) + mediaBytes, [notes, mediaBytes]);
  const important = useMemo(() => notes.filter(n => n.is_important == 1), [notes]); // eslint-disable-line eqeqeq
  const due = useMemo(() => reviewDue(notes, now), [notes, now]);
  const tree = useMemo(() => flattenTree(buildCategoryTree(categories)), [categories]);

  const countByCat = useMemo(() => {
    const m = new Map<number, number>();
    links.forEach(l => { if (l.is_deleted == 1) return; m.set(Number(l.category_id), (m.get(Number(l.category_id)) || 0) + 1); }); // eslint-disable-line eqeqeq
    return m;
  }, [links]);

  const recent = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notes
      .filter(n => !q || [n.title, n.content_text, n.tags, n.keywords].some(f => (f || '').toLowerCase().includes(q)))
      .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
      .slice(0, 12);
  }, [notes, search]);

  return (
    <div className="notes-page-inner">
      <div className="notes-stats">
        <div className="notes-stat"><FiFileText className="ns-icon" /><div><span className="ns-num">{notes.length}</span><span className="ns-label">Entries</span></div></div>
        <div className="notes-stat"><FiFolder className="ns-icon" /><div><span className="ns-num">{categories.length}</span><span className="ns-label">Categories</span></div></div>
        <div className="notes-stat"><FiStar className="ns-icon ns-star" /><div><span className="ns-num">{important.length}</span><span className="ns-label">Important</span></div></div>
        <div className="notes-stat"><FiBell className="ns-icon" /><div><span className="ns-num">{due.length}</span><span className="ns-label">Due to review</span></div></div>
        <div className="notes-stat"><FiHardDrive className="ns-icon" /><div><span className="ns-num">{formatBytes(totalBytes)}</span><span className="ns-label">Space{media.length ? ` · ${media.length} media` : ''}</span></div></div>
      </div>

      {due.length > 0 && (
        <div className="kv-report-block">
          <h3 className="notes-section-title"><FiClock size={13} /> Due for review</h3>
          <div className="kv-due-list">
            {due.slice(0, 8).map(n => (
              <div key={n.note_id} className="kv-due-item">
                <button className="kv-due-open" onClick={() => onOpenNote(n.note_id)}>
                  <span className="kv-due-title">{n.title || 'Untitled'}</span>
                  <span className="kv-due-when">{when(n.review_at)}</span>
                </button>
                <button className="kv-due-done" onClick={() => void markReviewed(n)} title="Mark reviewed — schedules the next review">
                  <FiCheck size={13} /> Reviewed
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="notes-body">
        <aside className="notes-cats">
          <h3 className="notes-section-title">Browse by category</h3>
          {tree.length === 0 && <div className="notes-cat-empty">No categories yet.</div>}
          {tree.map(({ cat, depth }) => (
            <button key={cat.category_id} className="notes-cat-row" style={{ paddingLeft: 10 + depth * 16 }} onClick={() => onOpenCategory(cat.category_id)}>
              <span className="ncr-name"><FiFolder size={13} style={{ color: cat.color || 'var(--gray-400)' }} /> {cat.category_name}</span>
              <span className="ncr-count">{countByCat.get(cat.category_id) || 0}</span>
            </button>
          ))}
        </aside>

        <section className="notes-list-wrap">
          <div className="notes-list-head">
            <div className="notes-search"><FiSearch size={15} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search the vault…" /></div>
          </div>
          <h3 className="notes-section-title">{search ? 'Results' : 'Recent'}</h3>
          {recent.length === 0 ? (
            <div className="notes-empty">{notes.length === 0 ? 'Your vault is empty — add entries in the Notes tab.' : 'No entries match your search.'}</div>
          ) : (
            <div className="kv-recent-list">
              {recent.map(n => (
                <button key={n.note_id} className="kv-recent-item" onClick={() => onOpenNote(n.note_id)}>
                  <span className="kv-recent-title">{n.is_pinned == 1 && '📌 '}{n.title || 'Untitled'}</span>{/* eslint-disable-line eqeqeq */}
                  <span className="kv-recent-prev">{(n.key_takeaway || n.content_text || '').slice(0, 90)}</span>
                  <span className="kv-recent-when">{when(n.updated_at)}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ReportsTab;
